import { gql } from "@apollo/client";
import { Box } from "@mui/material";
import { WidgetHeader } from "components/atoms/WidgetHeader";
import { WidgetPaper } from "components/atoms/WidgetPaper";
import { format } from "date-fns";
import { DisinfectionFilter, GroupBy } from "gql/graphql";
import { FormattedMessage } from "react-intl";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useQueryWithSnack } from "utils/useQueryWithSnack";
import { useDDFilter } from "../../modules/disinfectionFilter/DisinfectionFilterPanel";

interface Props {
  hiddenFilter?: DisinfectionFilter;
  groupBy?: GroupBy;
}

const QUERY = gql(`
  query DisinfectionsPerRoomAcrossTime($filter: DisinfectionFilter!, $groupBy: GroupBy!, $timezone: String!) {
    disinfectionCountPerRoom(filter: $filter, groupBy: $groupBy, timezone: $timezone) {
      date
      rooms {
        room
        complete
        incomplete
      }
    }
  }
`);

export const DisinfectionsPerRoomAcrossTimeGraph = ({
  hiddenFilter,
  groupBy = GroupBy.Day,
}: Props) => {
  const [filterParams] = useDDFilter();

  const { data } = useQueryWithSnack(QUERY, {
    variables: {
      filter: { ...filterParams, ...hiddenFilter },
      groupBy,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });

  const uniqueRooms = new Set<string>();
  data?.disinfectionCountPerRoom.forEach((entry: any) => {
    entry.rooms.forEach((room: any) =>
      uniqueRooms.add(room.room || "Current location"),
    );
  });

  const processedData =
    data?.disinfectionCountPerRoom.map((entry: any) => {
      const baseObj = Array.from(uniqueRooms).reduce(
        (acc, room) => ({
          ...acc,
          [room]: 0,
        }),
        {},
      );

      const roomValues = entry.rooms.reduce(
        (acc: any, room: any) => ({
          ...acc,
          [room.room || "Current location"]: room.complete + room.incomplete,
        }),
        baseObj,
      );

      return {
        date: format(new Date(entry.date), "dd MMM"),
        ...roomValues,
      };
    }) || [];

  const overallAverage = processedData.length
    ? processedData
        .flatMap((entry: any) =>
          Object.entries(entry)
            .filter(([key]) => key !== "date")
            .map(([, value]) => value as number),
        )
        .reduce((a: any, b: any) => a + b, 0) /
      (processedData.length * uniqueRooms.size)
    : 0;

  const dataWithAverage = processedData.map((entry: any) => ({
    ...entry,
    average: isNaN(overallAverage) ? 0 : Number(overallAverage.toFixed(2)),
  }));

  return (
    <WidgetPaper>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <WidgetHeader
          label={
            <FormattedMessage
              id="disinfection-by-room-graph"
              defaultMessage="Disinfections per room overview"
            />
          }
          description="Number of total daily disinfections per room across time"
        />
      </Box>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={dataWithAverage}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip
            position={{ x: -20, y: 200 }}
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                return (
                  <Box
                    sx={{
                      backgroundColor: "white",
                      border: "1px solid #ccc",
                      padding: "10px",
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(150px, 1fr))",
                      gap: "8px",
                      maxHeight: "600px",
                      maxWidth: "750px",
                    }}
                  >
                    <div style={{ fontWeight: "bold", gridColumn: "1 / -1" }}>
                      {label}
                    </div>
                    {payload
                      .sort((a: any, b: any) => {
                        if (a.name === "Daily average") return -1;
                        if (b.name === "Daily average") return 1;
                        return b.value - a.value;
                      })
                      .map((entry: any) => (
                        <div key={entry.name} style={{ color: entry.color }}>
                          {entry.name}: {entry.value}
                        </div>
                      ))}
                  </Box>
                );
              }
              return null;
            }}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="average"
            stroke="#000000"
            name="Daily average"
            strokeWidth={2}
            strokeDasharray="5 5"
            connectNulls={true}
            dot={false}
          />
          {Array.from(uniqueRooms).map((room, index) => (
            <Line
              key={room}
              type="monotone"
              dataKey={room}
              stroke={`hsl(${(index * 137 + 170) % 360}, 70%, 50%)`}
              name={room}
              strokeWidth={2}
              connectNulls={true}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </WidgetPaper>
  );
};
